<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-start mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">SBOM Portfolio</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Cross-SBOM dependency &amp; vulnerability overview, fed by EUVD + NVD.
        </p>
      </div>
      <div v-if="overview?.generated_at" class="text-xs text-gray-400 dark:text-gray-500">
        last refresh {{ formatRelative(overview.generated_at) }}
      </div>
    </div>

    <!-- Global product/version filter bar -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3 mb-5 flex flex-wrap items-center gap-3">
      <label class="text-xs font-medium text-gray-500 dark:text-gray-400">Filter:</label>
      <select v-model="globalProductId" @change="onProductFilterChange" class="input text-sm min-w-[180px]">
        <option value="">All products</option>
        <option v-for="p in productsList" :key="p.id" :value="p.id">{{ p.name }}</option>
      </select>
      <select v-model="globalVersionId" @change="onVersionFilterChange" :disabled="!globalProductId" class="input text-sm min-w-[140px]">
        <option value="">All versions</option>
        <option v-for="v in versionsList" :key="v.id" :value="v.id">v{{ v.version }}</option>
      </select>
      <button v-if="globalProductId" @click="clearFilters" class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline">Clear</button>
    </div>

    <!-- Tab nav -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav class="-mb-px flex space-x-6 overflow-x-auto">
        <button
          v-for="t in tabs"
          :key="t.id"
          @click="setTab(t.id)"
          :class="[
            'whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors',
            activeTab === t.id
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
          ]"
        >{{ t.label }}<span v-if="t.count !== null && t.count !== undefined" class="ml-1.5 text-xs text-gray-400">({{ t.count }})</span></button>
      </nav>
    </div>

    <!-- ============ OVERVIEW ============ -->
    <section v-if="activeTab === 'overview'">
      <div v-if="loading.overview" class="py-10 text-center text-gray-400">Loading overview…</div>
      <div v-else>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard label="SBOMs" :value="overview?.sbom_total ?? 0" />
          <KpiCard label="Components" :value="overview?.component_total ?? 0" />
          <KpiCard label="Open findings" :value="overview?.counts?.total ?? 0" tone="warning" />
          <KpiCard label="Actively exploited (KEV)" :value="overview?.counts?.kev ?? 0" tone="danger" />
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-5 mb-6">
          <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Severity breakdown</h2>
          <div class="flex h-3 rounded overflow-hidden border border-gray-200 dark:border-gray-700">
            <div v-for="b in severityBar" :key="b.label"
                 :class="b.cls" :style="`width:${b.pct}%`" :title="`${b.label}: ${b.value}`"></div>
          </div>
          <div class="flex flex-wrap gap-3 mt-3 text-xs">
            <span v-for="b in severityBar" :key="b.label" class="inline-flex items-center gap-1.5">
              <span class="inline-block w-2.5 h-2.5 rounded" :class="b.cls"></span>
              {{ b.label }} <strong class="text-gray-700 dark:text-gray-200">{{ b.value }}</strong>
            </span>
            <span v-if="(overview?.counts?.ignored ?? 0) > 0" class="text-gray-400 ml-auto">
              {{ overview.counts.ignored }} ignored (filtered out)
            </span>
          </div>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Top products by risk</h2>
            <span class="text-xs text-gray-400">10×Crit + 5×High + 2×Med + 1×Low</span>
          </div>
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th class="px-4 py-2 text-left">Product</th>
                <th class="px-4 py-2 text-right">Findings</th>
                <th class="px-4 py-2 text-right">Critical</th>
                <th class="px-4 py-2 text-right">High</th>
                <th class="px-4 py-2 text-right">Medium</th>
                <th class="px-4 py-2 text-right">Low</th>
                <th class="px-4 py-2 text-right">KEV</th>
                <th class="px-4 py-2 text-right">Risk</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-if="!overview?.products?.length">
                <td colspan="8" class="px-4 py-6 text-center text-gray-400">No findings yet — upload an SBOM to populate this view.</td>
              </tr>
              <tr v-for="p in overview?.products || []" :key="p.product_id"
                  class="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  @click="filterByProduct(p.product_id, p.product_name)">
                <td class="px-4 py-2 font-medium text-gray-800 dark:text-gray-200">{{ p.product_name }}</td>
                <td class="px-4 py-2 text-right">{{ p.findings }}</td>
                <td class="px-4 py-2 text-right text-red-600 dark:text-red-400">{{ p.critical }}</td>
                <td class="px-4 py-2 text-right text-orange-600 dark:text-orange-400">{{ p.high }}</td>
                <td class="px-4 py-2 text-right text-yellow-600 dark:text-yellow-400">{{ p.medium }}</td>
                <td class="px-4 py-2 text-right text-blue-600 dark:text-blue-400">{{ p.low }}</td>
                <td class="px-4 py-2 text-right">
                  <span v-if="p.kev > 0" class="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-semibold">{{ p.kev }}</span>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="px-4 py-2 text-right font-mono">{{ p.risk_score }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ============ SBOMs ============ -->
    <section v-else-if="activeTab === 'sboms'">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th class="px-4 py-2 text-left">SBOM</th>
              <th class="px-4 py-2 text-left">Product</th>
              <th class="px-4 py-2 text-left">Version</th>
              <th class="px-4 py-2 text-left">Format</th>
              <th class="px-4 py-2 text-right">Components</th>
              <th class="px-4 py-2 text-right">Findings</th>
              <th class="px-4 py-2 text-right">Critical</th>
              <th class="px-4 py-2 text-right">High</th>
              <th class="px-4 py-2 text-left">Uploaded</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading.sboms"><td colspan="9" class="py-6 text-center text-gray-400">Loading…</td></tr>
            <tr v-else-if="!sboms.length"><td colspan="9" class="py-6 text-center text-gray-400">No SBOMs uploaded yet.</td></tr>
            <tr v-for="s in sboms" :key="s.sbom_id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <td class="px-4 py-2 text-xs font-medium text-gray-800 dark:text-gray-200">{{ s.sbom_name || `SBOM #${s.sbom_id}` }}</td>
              <td class="px-4 py-2 text-xs">{{ s.product_name || '—' }}</td>
              <td class="px-4 py-2 text-xs">{{ s.version_name ? `v${s.version_name}` : '—' }}</td>
              <td class="px-4 py-2 text-xs text-gray-500">{{ s.format || '—' }}</td>
              <td class="px-4 py-2 text-right text-xs">{{ s.components }}</td>
              <td class="px-4 py-2 text-right text-xs" :class="s.findings > 0 ? 'font-semibold text-amber-600 dark:text-amber-400' : ''">{{ s.findings }}</td>
              <td class="px-4 py-2 text-right text-xs text-red-600 dark:text-red-400">{{ s.critical || '—' }}</td>
              <td class="px-4 py-2 text-right text-xs text-orange-600 dark:text-orange-400">{{ s.high || '—' }}</td>
              <td class="px-4 py-2 text-xs text-gray-500">{{ formatDate(s.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ============ FINDINGS ============ -->
    <section v-else-if="activeTab === 'findings'">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input v-model="filters.search" @input="debouncedFetchFindings"
               placeholder="Search CVE id…" class="input text-sm" />
        <select v-model="filters.severity" @change="fetchFindings" class="input text-sm">
          <option value="">All severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select v-model="filters.source" @change="fetchFindings" class="input text-sm">
          <option value="">All sources</option>
          <option value="EUVD">EUVD</option>
          <option value="NVD">NVD</option>
          <option value="INTERNAL">Internal</option>
        </select>
        <label class="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" v-model="filters.kev" @change="fetchFindings" /> KEV only
        </label>
        <label class="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" v-model="filters.ignored" @change="fetchFindings" /> Show ignored
        </label>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th class="px-4 py-2 text-left">CVE</th>
              <th class="px-4 py-2 text-left">Severity</th>
              <th class="px-4 py-2 text-left">Component</th>
              <th class="px-4 py-2 text-left">Product / Version</th>
              <th class="px-4 py-2 text-left">Source</th>
              <th class="px-4 py-2 text-left">First seen</th>
              <th class="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading.findings"><td colspan="7" class="py-6 text-center text-gray-400">Loading…</td></tr>
            <tr v-else-if="!findings.length"><td colspan="7" class="py-6 text-center text-gray-400">No findings match the current filters.</td></tr>
            <tr v-for="f in findings" :key="f.id" :class="['hover:bg-gray-50 dark:hover:bg-gray-700/50', f.ignored ? 'opacity-60' : '']">
              <td class="px-4 py-2 font-mono text-xs">
                <a :href="cveLink(f)" target="_blank" rel="noopener" class="text-blue-600 dark:text-blue-400 hover:underline">{{ f.cve_id }}</a>
                <span v-if="f.kev" class="ml-1 px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-bold align-middle">KEV</span>
              </td>
              <td class="px-4 py-2"><SeverityBadge :sev="f.severity" /></td>
              <td class="px-4 py-2 text-xs">
                <div class="font-mono">{{ f.component_name }}<span class="text-gray-400">@</span>{{ f.component_version }}</div>
                <div v-if="f.component_purl" class="text-[11px] text-gray-400 truncate max-w-[280px]" :title="f.component_purl">{{ f.component_purl }}</div>
              </td>
              <td class="px-4 py-2 text-xs">
                <span v-if="f.product_name">{{ f.product_name }}</span>
                <span v-else class="text-gray-400">—</span>
                <span v-if="f.version_name" class="text-gray-400"> · {{ f.version_name }}</span>
              </td>
              <td class="px-4 py-2 text-xs">{{ f.source }}</td>
              <td class="px-4 py-2 text-xs text-gray-500">{{ formatDate(f.first_seen_at) }}</td>
              <td class="px-4 py-2 text-right space-x-2">
                <button v-if="!f.ignored" @click="ignoreFinding(f, true)" class="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">Ignore</button>
                <button v-else @click="ignoreFinding(f, false)" class="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">Un-ignore</button>
                <button v-if="!f.promoted_vuln_id" @click="promote(f)" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">Promote</button>
                <span v-else class="text-xs text-green-600 dark:text-green-400">Promoted</span>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination
          v-if="findingsResp.totalPages > 1"
          :current-page="findingsResp.currentPage"
          :total-pages="findingsResp.totalPages"
          @page-change="onFindingsPage"
        />
      </div>
    </section>

    <!-- ============ COMPONENTS ============ -->
    <section v-else-if="activeTab === 'components'">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 flex gap-3">
        <input v-model="filters.compSearch" @input="debouncedFetchComponents"
               placeholder="Search component name or PURL…" class="input text-sm flex-1" />
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th class="px-4 py-2 text-left">Component</th>
              <th class="px-4 py-2 text-left">Version</th>
              <th class="px-4 py-2 text-left">License</th>
              <th class="px-4 py-2 text-right">Used in SBOMs</th>
              <th class="px-4 py-2 text-right">Findings</th>
              <th class="px-4 py-2 text-right">Critical</th>
              <th class="px-4 py-2 text-right">High</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading.components"><td colspan="7" class="py-6 text-center text-gray-400">Loading…</td></tr>
            <tr v-else-if="!components.length"><td colspan="7" class="py-6 text-center text-gray-400">No components found.</td></tr>
            <tr v-for="(c, i) in components" :key="i" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <td class="px-4 py-2 text-xs">
                <div class="font-mono">{{ c.name }}</div>
                <div v-if="c.purl" class="text-[11px] text-gray-400 truncate max-w-[320px]" :title="c.purl">{{ c.purl }}</div>
              </td>
              <td class="px-4 py-2 text-xs font-mono">{{ c.version || '—' }}</td>
              <td class="px-4 py-2 text-xs">{{ c.license || '—' }}</td>
              <td class="px-4 py-2 text-right text-xs">{{ c.sboms }}</td>
              <td class="px-4 py-2 text-right text-xs">{{ c.findings }}</td>
              <td class="px-4 py-2 text-right text-xs text-red-600 dark:text-red-400">{{ c.critical }}</td>
              <td class="px-4 py-2 text-right text-xs text-orange-600 dark:text-orange-400">{{ c.high }}</td>
            </tr>
          </tbody>
        </table>
        <Pagination
          v-if="componentsResp.totalPages > 1"
          :current-page="componentsResp.currentPage"
          :total-pages="componentsResp.totalPages"
          @page-change="onComponentsPage"
        />
      </div>
    </section>

    <!-- ============ VULNERABILITIES ============ -->
    <section v-else-if="activeTab === 'vulnerabilities'">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th class="px-4 py-2 text-left">CVE</th>
              <th class="px-4 py-2 text-left">Severity</th>
              <th class="px-4 py-2 text-right">CVSS</th>
              <th class="px-4 py-2 text-left">Source</th>
              <th class="px-4 py-2 text-right">Findings</th>
              <th class="px-4 py-2 text-right">Components</th>
              <th class="px-4 py-2 text-right">Products</th>
              <th class="px-4 py-2 text-left">First seen</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading.vulns"><td colspan="8" class="py-6 text-center text-gray-400">Loading…</td></tr>
            <tr v-else-if="!vulns.length"><td colspan="8" class="py-6 text-center text-gray-400">No vulnerabilities detected.</td></tr>
            <template v-for="v in vulns" :key="v.cve_id">
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" @click="toggleVuln(v.cve_id)">
                <td class="px-4 py-2 font-mono text-xs">
                  {{ v.cve_id }}
                  <span v-if="v.kev" class="ml-1 px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-bold">KEV</span>
                </td>
                <td class="px-4 py-2"><SeverityBadge :sev="v.severity" /></td>
                <td class="px-4 py-2 text-right text-xs font-mono">{{ v.cvss_score?.toFixed?.(1) ?? '—' }}</td>
                <td class="px-4 py-2 text-xs">{{ v.source }}</td>
                <td class="px-4 py-2 text-right text-xs">{{ v.findings }}</td>
                <td class="px-4 py-2 text-right text-xs">{{ v.components }}</td>
                <td class="px-4 py-2 text-right text-xs">{{ v.products }}</td>
                <td class="px-4 py-2 text-xs text-gray-500">{{ formatDate(v.first_seen) }}</td>
              </tr>
              <tr v-if="expandedCve === v.cve_id" class="bg-gray-50 dark:bg-gray-900/40">
                <td colspan="8" class="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">
                  <p>{{ v.description || 'No description available from advisory feeds.' }}</p>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

const SeverityBadge = {
  props: ['sev'],
  template: `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="cls">{{ label }}</span>`,
  computed: {
    label() { return (this.sev || '').toString().toUpperCase() || '—' },
    cls() {
      const s = (this.sev || '').toString().toUpperCase()
      return {
        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': s === 'CRITICAL',
        'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200': s === 'HIGH',
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': s === 'MEDIUM',
        'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': s === 'LOW',
        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300': !['CRITICAL','HIGH','MEDIUM','LOW'].includes(s)
      }
    }
  }
}

const KpiCard = {
  props: ['label', 'value', 'tone'],
  template: `<div :class="['rounded-lg shadow p-4', toneCls]">
    <div class="text-xs uppercase tracking-wide opacity-80">{{ label }}</div>
    <div class="text-2xl font-bold mt-1">{{ value }}</div>
  </div>`,
  computed: {
    toneCls() {
      if (this.tone === 'danger') return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200'
      if (this.tone === 'warning') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
      return 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200'
    }
  }
}

const Pagination = {
  props: ['currentPage', 'totalPages'],
  emits: ['page-change'],
  template: `<div class="flex justify-end items-center gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-xs">
    <button @click="$emit('page-change', currentPage - 1)" :disabled="currentPage <= 1"
            class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Prev</button>
    <span class="text-gray-500">Page {{ currentPage }} / {{ totalPages }}</span>
    <button @click="$emit('page-change', currentPage + 1)" :disabled="currentPage >= totalPages"
            class="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Next</button>
  </div>`
}

export default {
  name: 'PortfolioView',
  components: { SeverityBadge, KpiCard, Pagination },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const API = getApiBase()
    const tenantId = computed(() => route.params.tenantId || store.state.currentTenantId)
    const headers = () => ({ Authorization: `Bearer ${store.state.token}` })

    const validTabs = ['overview', 'sboms', 'findings', 'components', 'vulnerabilities']
    const activeTab = ref(validTabs.includes(route.params.tab) ? route.params.tab : 'overview')

    // --- data ---
    const overview = ref(null)
    const sboms = ref([])
    const findings = ref([])
    const findingsResp = reactive({ currentPage: 1, totalPages: 1, perPage: 25 })
    const components = ref([])
    const componentsResp = reactive({ currentPage: 1, totalPages: 1, perPage: 25 })
    const vulns = ref([])
    const expandedCve = ref(null)

    const loading = reactive({ overview: false, sboms: false, findings: false, components: false, vulns: false })
    const filters = reactive({
      search: '', severity: '', source: '', kev: false, ignored: false,
      compSearch: '',
    })

    // Track which tabs have been fetched so we don't skip zero-result tabs.
    const fetched = reactive({ overview: false, sboms: false, findings: false, components: false, vulns: false })

    // --- global product/version filter ---
    const productsList = ref([])
    const versionsList = ref([])
    const globalProductId = ref('')
    const globalVersionId = ref('')

    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/products`, { headers: headers(), params: { per_page: 200 } })
        productsList.value = data?.data || data?.products || (Array.isArray(data) ? data : [])
      } catch { productsList.value = [] }
    }

    const fetchVersions = async (productId) => {
      if (!productId) { versionsList.value = []; return }
      try {
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/products/${productId}/versions`, { headers: headers(), params: { per_page: 200 } })
        versionsList.value = Array.isArray(data) ? data : (data?.data || [])
      } catch { versionsList.value = [] }
    }

    const onProductFilterChange = () => {
      globalVersionId.value = ''
      if (globalProductId.value) fetchVersions(globalProductId.value)
      else versionsList.value = []
      refreshAll()
    }
    const onVersionFilterChange = () => refreshAll()
    const clearFilters = () => {
      globalProductId.value = ''
      globalVersionId.value = ''
      versionsList.value = []
      refreshAll()
    }
    const filterByProduct = (pid) => {
      globalProductId.value = String(pid)
      fetchVersions(pid)
      refreshAll()
    }

    // --- tabs ---
    const tabs = computed(() => [
      { id: 'overview', label: 'Overview', count: null },
      { id: 'sboms', label: 'SBOMs', count: overview.value?.sbom_total ?? null },
      { id: 'findings', label: 'Findings', count: overview.value?.counts?.total ?? null },
      { id: 'components', label: 'Components', count: overview.value?.component_total ?? null },
      { id: 'vulnerabilities', label: 'Vulnerabilities', count: vulns.value?.length || null },
    ])

    const severityBar = computed(() => {
      const c = overview.value?.counts || {}
      const total = Math.max(1, (c.critical || 0) + (c.high || 0) + (c.medium || 0) + (c.low || 0))
      const pct = (n) => Math.max(0, ((n || 0) / total) * 100)
      return [
        { label: 'Critical', value: c.critical || 0, pct: pct(c.critical), cls: 'bg-red-500' },
        { label: 'High',     value: c.high     || 0, pct: pct(c.high),     cls: 'bg-orange-500' },
        { label: 'Medium',   value: c.medium   || 0, pct: pct(c.medium),   cls: 'bg-yellow-400' },
        { label: 'Low',      value: c.low      || 0, pct: pct(c.low),      cls: 'bg-blue-400' },
      ]
    })

    const setTab = (id) => {
      activeTab.value = id
      router.replace({ name: 'PortfolioTab', params: { tenantId: tenantId.value, tab: id } })
      loadActiveTab()
    }

    // Refresh everything when global filter changes.
    const refreshAll = () => {
      Object.keys(fetched).forEach(k => fetched[k] = false)
      overview.value = null
      sboms.value = []
      findings.value = []
      components.value = []
      vulns.value = []
      fetchOverview()
      loadActiveTab()
    }

    const loadActiveTab = () => {
      const tab = activeTab.value
      if (tab === 'overview' && !fetched.overview) fetchOverview()
      else if (tab === 'sboms' && !fetched.sboms) fetchSBOMs()
      else if (tab === 'findings' && !fetched.findings) fetchFindings()
      else if (tab === 'components' && !fetched.components) fetchComponents()
      else if (tab === 'vulnerabilities' && !fetched.vulns) fetchVulns()
    }

    // --- API calls ---
    const filterParams = () => {
      const p = {}
      if (globalProductId.value) p.product_id = globalProductId.value
      if (globalVersionId.value) p.version_id = globalVersionId.value
      return p
    }

    const fetchOverview = async () => {
      loading.overview = true
      try {
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/portfolio/overview`, { headers: headers() })
        overview.value = data
        fetched.overview = true
      } catch (e) { notifyError(e, 'Failed to load overview') }
      finally { loading.overview = false }
    }

    const fetchSBOMs = async () => {
      loading.sboms = true
      try {
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/portfolio/sboms`, { headers: headers(), params: filterParams() })
        sboms.value = data.sboms || []
        fetched.sboms = true
      } catch (e) { notifyError(e, 'Failed to load SBOMs') }
      finally { loading.sboms = false }
    }

    const fetchFindings = async (page = 1) => {
      loading.findings = true
      try {
        const params = {
          ...filterParams(),
          page, per_page: findingsResp.perPage,
          search: filters.search || undefined,
          severity: filters.severity || undefined,
          source: filters.source || undefined,
          kev: filters.kev ? 'true' : undefined,
          ignored: filters.ignored ? 'true' : 'false',
        }
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/sbom-findings`, { headers: headers(), params })
        findings.value = data.data || []
        findingsResp.currentPage = data.currentPage || 1
        findingsResp.totalPages = data.totalPages || 1
        fetched.findings = true
      } catch (e) { notifyError(e, 'Failed to load findings') }
      finally { loading.findings = false }
    }
    let findingsTimer = null
    const debouncedFetchFindings = () => {
      clearTimeout(findingsTimer)
      findingsTimer = setTimeout(() => fetchFindings(1), 300)
    }
    const onFindingsPage = (p) => fetchFindings(p)

    const fetchComponents = async (page = 1) => {
      loading.components = true
      try {
        const params = { ...filterParams(), page, per_page: componentsResp.perPage, search: filters.compSearch || undefined }
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/portfolio/components`, { headers: headers(), params })
        components.value = data.data || []
        componentsResp.currentPage = data.currentPage || 1
        componentsResp.totalPages = Math.max(1, Math.ceil((data.totalEntries || 0) / componentsResp.perPage))
        fetched.components = true
      } catch (e) { notifyError(e, 'Failed to load components') }
      finally { loading.components = false }
    }
    let compTimer = null
    const debouncedFetchComponents = () => {
      clearTimeout(compTimer)
      compTimer = setTimeout(() => fetchComponents(1), 300)
    }
    const onComponentsPage = (p) => fetchComponents(p)

    const fetchVulns = async () => {
      loading.vulns = true
      try {
        const { data } = await axios.get(`${API}/admin/tenants/${tenantId.value}/portfolio/vulnerabilities`, { headers: headers() })
        vulns.value = data.vulnerabilities || []
        fetched.vulns = true
      } catch (e) { notifyError(e, 'Failed to load vulnerabilities') }
      finally { loading.vulns = false }
    }

    const toggleVuln = (id) => { expandedCve.value = expandedCve.value === id ? null : id }

    // --- actions ---
    const ignoreFinding = async (f, ignored) => {
      const reason = ignored ? (window.prompt('Optional reason for ignoring?') || '') : ''
      try {
        await axios.put(`${API}/admin/tenants/${tenantId.value}/sbom-findings/${f.id}/ignore`,
          { ignored, reason }, { headers: headers() })
        f.ignored = ignored
        f.ignored_reason = reason
        fetched.overview = false
        fetchOverview()
      } catch (e) { notifyError(e, 'Update failed') }
    }

    const promote = async (f) => {
      const msg = f.kev
        ? `Promote ${f.cve_id} to a tracked vulnerability?\n\n(KEV: an incident will also be auto-created for CRA Article-14 reporting.)`
        : `Promote ${f.cve_id} to a tracked vulnerability?`
      if (!confirm(msg)) return
      try {
        const { data } = await axios.post(`${API}/admin/tenants/${tenantId.value}/sbom-findings/${f.id}/promote`,
          {}, { headers: headers() })
        f.promoted_vuln_id = data.vuln_id
        store.dispatch('notification/showNotification', { type: 'success', message: `Promoted to ${data.vuln_public_id}`, duration: 4000 })
      } catch (e) { notifyError(e, 'Promote failed') }
    }

    // --- helpers ---
    const cveLink = (f) => {
      if (!f.cve_id) return '#'
      if (f.cve_id.startsWith('EUVD-')) return `https://euvd.enisa.europa.eu/vulnerability/${encodeURIComponent(f.cve_id)}`
      return `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(f.cve_id)}`
    }
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—'
    const formatRelative = (d) => {
      if (!d) return ''
      const diff = (Date.now() - new Date(d).getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      return new Date(d).toLocaleString()
    }
    const notifyError = (e, fallback) => {
      store.dispatch('notification/showNotification', {
        type: 'error',
        message: e?.response?.data?.error || fallback,
        duration: 5000,
      })
    }

    onMounted(() => {
      fetchProducts()
      fetchOverview()
      loadActiveTab()
    })
    watch(() => route.params.tab, (t) => {
      if (validTabs.includes(t) && t !== activeTab.value) {
        activeTab.value = t
        loadActiveTab()
      }
    })

    return {
      activeTab, tabs, setTab,
      overview, severityBar,
      sboms, fetchSBOMs,
      findings, findingsResp, fetchFindings, debouncedFetchFindings, onFindingsPage,
      components, componentsResp, fetchComponents, debouncedFetchComponents, onComponentsPage,
      vulns, expandedCve, toggleVuln,
      filters, loading,
      productsList, versionsList, globalProductId, globalVersionId,
      onProductFilterChange, onVersionFilterChange, clearFilters, filterByProduct,
      ignoreFinding, promote,
      cveLink, formatDate, formatRelative,
    }
  }
}
</script>
